/*
	React Router Future Flag Warning:
	React Router will begin wrapping state updates in `React.startTransition` in v7.
	You can use the `v7_startTransition` future flag to opt-in early.
	For more information, see https://reactrouter.com/v6/upgrading/future#v7_starttransition.
	Porbably createBrowserRouter should be replaced by BrowserRouter.
*/

import ErrorBoundary from 'components/ErrorBoundary';
import ErrorFallback from 'components/feedback/ErrorFallback';
import AppLayout from 'layout/AppLayout';
import SettingsLayout from 'layout/SettingsLayout';
import SimulationsLayout from 'layout/SimulationsLayout';
import ErrorPage from 'pages/ErrorPage';
import SettingsPage from 'pages/SettingsPage';
import SimulationsPage from 'pages/SimulationsPage';
import DomainSimulationPage from 'pages/DomainSimulationPage';
import SimulationsV2Page from 'pages/SimulationsV2Page';
import RouteBuilderPage from 'pages/RouteBuilderPage';
import { createBrowserRouter, Navigate } from 'react-router-dom';

import MainPageWrapper from './MainPageWrapper';
import { ProtectedRoute } from './ProtectedRoute';
import { ROUTES } from './types';

export const router = createBrowserRouter([
    {
        path: ROUTES.DEFAULT,
        errorElement: <ErrorBoundary FallbackComponent={ErrorFallback} />,
        element: <AppLayout />,
        children: [
            {
                index: true,
                errorElement: <ErrorBoundary FallbackComponent={ErrorFallback} />,
                element: (
                    <ProtectedRoute route={ROUTES.DEFAULT}>
                        <MainPageWrapper />
                    </ProtectedRoute>
                ),
            },
            {
                path: '*',
                element: (
                    <ProtectedRoute route={ROUTES.DEFAULT}>
                        <Navigate to={ROUTES.DEFAULT} replace />
                    </ProtectedRoute>
                ),
            },
            {
                path: ROUTES.VEHICLE,
                errorElement: <ErrorBoundary FallbackComponent={ErrorFallback} />,
                lazy: async () => {
                    const { default: NoVehicleSelected } = await import('pages/vehicle/NoVehicleSelected');
                    return {
                        element: (
                            <ProtectedRoute route={ROUTES.VEHICLE}>
                                <NoVehicleSelected />
                            </ProtectedRoute>
                        ),
                    };
                },
            },
            {
                path: ROUTES.VEHICLE_BY_VIN,
                errorElement: <ErrorBoundary FallbackComponent={ErrorFallback} />,
                lazy: async () => {
                    const { default: VehiclePage } = await import('pages/vehicle/VehiclePage');
                    return {
                        element: (
                            <ProtectedRoute route={ROUTES.VEHICLE_BY_VIN}>
                                <VehiclePage />
                            </ProtectedRoute>
                        ),
                    };
                },
            },
        ],
    },
    {
        path: ROUTES.SIMULATIONS,
        errorElement: <ErrorBoundary FallbackComponent={ErrorFallback} />,
        element: <SimulationsLayout />,
        children: [
            {
                index: true,
                element: <Navigate to='domain' replace />,
            },
            {
                path: 'v1',
                errorElement: <ErrorBoundary FallbackComponent={ErrorFallback} />,
                element: (
                    <ProtectedRoute route={ROUTES.SIMULATIONS_V1}>
                        <SimulationsPage />
                    </ProtectedRoute>
                ),
            },
            {
                path: 'v2',
                errorElement: <ErrorBoundary FallbackComponent={ErrorFallback} />,
                element: (
                    <ProtectedRoute route={ROUTES.SIMULATIONS_V2}>
                        <SimulationsV2Page />
                    </ProtectedRoute>
                ),
            },
            {
                path: 'domain',
                errorElement: <ErrorBoundary FallbackComponent={ErrorFallback} />,
                element: (
                    <ProtectedRoute route={ROUTES.SIMULATIONS_DOMAIN}>
                        <DomainSimulationPage />
                    </ProtectedRoute>
                ),
            },
            {
                path: 'route-builder',
                errorElement: <ErrorBoundary FallbackComponent={ErrorFallback} />,
                element: (
                    <ProtectedRoute route={ROUTES.SIMULATIONS_ROUTE_BUILDER}>
                        <RouteBuilderPage />
                    </ProtectedRoute>
                ),
            },
        ],
    },
    {
        path: ROUTES.SETTINGS,
        errorElement: <ErrorBoundary FallbackComponent={ErrorFallback} />,
        element: <SettingsLayout />,
        children: [
            {
                index: true,
                errorElement: <ErrorBoundary FallbackComponent={ErrorFallback} />,
                element: (
                    <ProtectedRoute route={ROUTES.SETTINGS}>
                        <SettingsPage />
                    </ProtectedRoute>
                ),
            },
        ],
    },
    {
        path: ROUTES.ERROR,
        element: <SettingsLayout />,
        children: [
            {
                index: true,
                errorElement: <ErrorBoundary FallbackComponent={ErrorFallback} />,
                element: <ErrorPage />,
            },
        ],
    },
    {
        path: '*',
        element: <Navigate to={ROUTES.DEFAULT} replace />,
    },
]);
